import { Injectable } from '@angular/core';

import { Observable, of, BehaviorSubject, Subscription } from 'rxjs';
import { filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { TargetingSearchesApiService } from '@api/targeting/targeting-searches.api.service';
import { TargetingListsApiService } from '@api/targeting/targeting-lists.api.service';
import { LocalizedEnum } from '@api/journodex/model/localized-enum';
import { TargetingSearchResponseApiModel } from '@api/targeting/model/targeting-search-response.api.model';
import { ExcludedRecipientApiModel } from '@api/distributions/model/excluded-recipient.api.model';
import { TargetingSearchApi } from '@api/targeting/model/targeting-search.api.model';
import { TargetingListApi } from '@api/targeting/model/targeting-list-api.model';
import { TargetingAccountPreferencesApiModel } from '@api/targeting/model/targeting-account-preferences.api.model';

import { PageCriteriaModelInterface } from '@Common/Misc/models/page-criteria.model.interface';
import { FilterTypeModel } from '@Common/Filter/models/abstract-filter-type.model';
import { JournodexSubjectsService } from '@Common/Journodex/services/journodex-subjects.service';
import { JournodexRegionsService } from '@Common/Journodex/services/journodex-regions.service';
import { SortFieldEnum } from '@Common/TargetingShared/enum/SortField.enum';
import { IdNamePairModel } from '@Common/Misc/models/id-name-pair.model';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { TargetingPreferencesService } from '@Common/TargetingShared/services/targeting-preferences.service';
import { IncludeExcludeModel } from '@Common/Misc/models/include-exclude-model';

import { promiseOnlyResolved } from '@Core/utils';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';

import { TargetingSearch } from '@Discover/Targeting/models/targeting-search.model';
import { TargetingResultModel } from '@Discover/Targeting/models/targeting-result.model';
import { JournodexEnumsService } from '@Discover/Core/services/journodex-enums.service';
import { BaseSearchService, TargetingFacetCollection } from '@Discover/Targeting/services/base-search.service';
import { TargetingResultFactory } from '@Discover/Targeting/factories/targeting-result.factory';
import { RecipientInformationModel } from '@Discover/Targeting/models/recipient-information.model';
import { TargetingListTypeEnum } from '@Discover/Targeting/enums/targeting-list-type.enum';
import { RecipientType } from '@Discover/Targeting/enums/recipient-type.enum';
import { RecipientGroup } from '@Discover/Targeting/models/recipient-group.model';

import { RecipientEditorService, RecipientRow } from '../recipient-editor.service';
import { DistributionApi } from '@api/distributions/distribution.api';

@Injectable()
export class RecommendedRecipientService extends BaseSearchService {

    protected nRecipients$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    protected nResults$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

    protected search: TargetingSearch = null;
    protected search$: BehaviorSubject<TargetingSearch> = new BehaviorSubject<TargetingSearch>(this.search);

    // cached results, seperated by distribution ID... because we don't want to "re-get" the recommended recipients
    protected cashedResults: { [key: string]: TargetingResultModel[] } = {};
    private cashedResults$: BehaviorSubject<TargetingResultModel[]> = new BehaviorSubject<TargetingResultModel[]>(null);

    protected cachedSearch: { [key: string]: TargetingSearch } = {};
    protected cachedSearch$: BehaviorSubject<TargetingSearch> = new BehaviorSubject<TargetingSearch>(null);

    protected cachedRecipientCount: { [key: string]: number } = {};
    protected cachedRecipientCount$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

    protected quickEditLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    protected quickEditSearch: TargetingSearch = null;
    protected quickEditSearch$: BehaviorSubject<TargetingSearch> = new BehaviorSubject<TargetingSearch>(this.quickEditSearch);

    isQuickEdit: boolean = false;
    protected isQuickEditResponse$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private quickEditPreviewCount$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    private quickEditPreviewCountLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    contactSortFields: LocalizedEnum[] = [
        {
            enumValue: SortFieldEnum.CIRCULATION_UNIQUE_VISITORS,
            displayString: 'Discover.targeting.sortFields.uniqueVisitors'
        },
        {
            enumValue: SortFieldEnum.FIRST_NAME,
            displayString: 'Discover.targeting.sortFields.firstName'
        },
        {
            enumValue: SortFieldEnum.LAST_NAME,
            displayString: 'Discover.targeting.sortFields.lastName'
        },
        {
            enumValue: SortFieldEnum.JOB_TITLE,
            displayString: 'Discover.targeting.sortFields.jobTitle'
        },
        {
            enumValue: SortFieldEnum.OUTLET_NAME,
            displayString: 'Discover.targeting.sortFields.outletName'
        },
        {
            enumValue: SortFieldEnum.COUNTRY_NAME,
            displayString: 'Common.country'
        },
        {
            enumValue: SortFieldEnum.PROVINCE_NAME,
            displayString: 'Common.province'
        },
        {
            enumValue: SortFieldEnum.CITY_NAME,
            displayString: 'Common.city'
        }
    ];
    excludedRecipients: ExcludedRecipientApiModel[];
    viewExclusions: boolean = false;
    cachedFilters: TargetingSearch;

    recipientInformation: RecipientInformationModel;
    recommenedRecipientGroup: RecipientGroup;

    private targetingAccountPreferences: TargetingAccountPreferencesApiModel = null;

    distributionId: number;

    readonly defaultPageCriteria = {
        page: 1,
        pageSize: 200
    };

    protected recommendedRecipientRequest: Subscription;

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingResultFactory: TargetingResultFactory,
        protected recipientEditorService: RecipientEditorService,
        protected currentUserStateService: CurrentUserStateService,
        protected distributionService: DistributionService,
        private messageService: MessageService,
        private targetingPreferencesService: TargetingPreferencesService,
        protected distributionApiService: DistributionApi
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApiService
        );
        this.setFilters(this.allFilters);
        this.pageCriteria = this.defaultPageCriteria;
        this.pageCriteria$.next(this.pageCriteria);

        this.targetingSortFields$.next(this.contactSortFields);
        this.sortField = this.contactSortFields[0];
        this.sortField$.next(this.contactSortFields[0]);

        this.subscriptions.push(
            this.recipientEditorService.getExcludedRecipients().subscribe(x => this.excludedRecipients = x),
            this.distributionService.getFocusedResource().pipe(
                filter(x => !!x),
            ).subscribe(distribution => {
                this.distributionId = distribution.id;
            }),
            this.recipientEditorService.getCurrentRecipientInformation().pipe(filter(x => !!x)).subscribe(recipientInfo => {
                this.recommenedRecipientGroup = recipientInfo?.getRecipientGroup(RecipientType.recommended);
                this.recipientInformation = recipientInfo;
            }),
            this.targetingPreferencesService.targetingPreferences$.pipe(filter(x => !!x)).subscribe(preferences => {
                this.targetingAccountPreferences = preferences;
            }),
        );
    }
    getRecommendedRecipientQuery(id: number) {
        this.searchLoading$.next(true);

        this.nRecipients$.next(0);
        this.nResults = 0;
        this.nResults$.next(this.nResults);

        this.distributionApiService.getRecommendedRecipientQuery(id).subscribe({
            next: (query) => {
                const search = {} as TargetingSearchApi;
                if (this.targetingAccountPreferences) {
                    if (!query.geography) {
                        query.geography = new IncludeExcludeModel();
                    }
                    query.geography.merge(this.targetingAccountPreferences.geography);
                }
                search.query = query;

                this.search = new TargetingSearch(search);
                this.search.isRecommendedRecipientSearch =  true;
                this.search$.next(this.search);
                this.cachedSearch[this.distributionId] = this.search;
                this.doSearch(true, true);
            },
            error: (err) => {
                this.searchLoading$.next(false);
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.recommendedRecipient.recipient.query.header',
                    detail: 'Discover.recommendedRecipient.recipient.query.error'
                });
            }
        });
    }

    getSearchFromQuickEdit(search: TargetingSearch) {
        this.search = search;
        if (this.targetingAccountPreferences) {
            if (!search.geographies) {
                search.geographies = new IncludeExcludeModel();
            }
            search.geographies.merge(new IncludeExcludeModel(this.targetingAccountPreferences.geography));
        }
        this.search.isRecommendedRecipientSearch =  true;
        this.search$.next(this.search);
        this.isQuickEdit = true;
        this.isQuickEditResponse$.next(this.isQuickEdit);
        this.doSearch(true, true);
    }

    fetchPreviewCount(search: TargetingSearch) {
        if (this.targetingAccountPreferences) {
            if (!search.geographies) {
                search.geographies = new IncludeExcludeModel();
            }
            search.geographies.merge(new IncludeExcludeModel(this.targetingAccountPreferences.geography));
        }
        search.isRecommendedRecipientSearch =  true;

        if (!search || !search.hasAnyFilters(search.searchMode)) {
            this.quickEditPreviewCount$.next(null);
            return;
        }
        this.quickEditPreviewCountLoading$.next(true);

        const query = search.toApiModel(false, null).query;
        this.distributionApiService.performRecommendedRecipientSearch(query, 0, 0)
            .pipe(take(1))
            .subscribe({
                next: (x) => {
                    this.quickEditPreviewCount$.next(x.numResults);
                    this.quickEditPreviewCountLoading$.next(false);
                },
                error: () => {
                    this.quickEditPreviewCountLoading$.next(false);
                }
            });
    }

    getQuickEditQuery(id: number) {
        this.quickEditLoading$.next(true);

        this.distributionApiService.getQuickEditQuery(id).subscribe({
            next: (query) => {
                const search = {} as TargetingSearchApi;
                if (this.targetingAccountPreferences) {
                    if (!query.geography) {
                        query.geography = new IncludeExcludeModel();
                    }
                    query.geography.merge(this.targetingAccountPreferences.geography);
                }
                search.query = query;

                this.quickEditSearch = new TargetingSearch(search);
                this.quickEditSearch.isRecommendedRecipientSearch =  true;
                this.quickEditLoading$.next(false);
                this.quickEditSearch$.next(this.quickEditSearch);
            },
            error: (err) => {
                this.quickEditLoading$.next(false);
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.recommendedRecipient.quickEdit.query.header',
                    detail: 'Discover.recommendedRecipient.quickEdit.query.error'
                });
            }
        });
    }

    getQuickEditLoading(): Observable<boolean> {
        return this.quickEditLoading$;
    }

    getQuickEditSearch(): Observable<TargetingSearch> {
        return this.quickEditSearch$;
    }

    getFilterDefinitions(): FilterTypeModel[] {
        const filters: FilterTypeModel[] = [
            this.audienceReachFilter,
            this.subjectFilter,
            this.jobRoleFilter,
            this.jobTitleFilter,
            this.languageFilter,
            this.locationFilter,
            this.contactNameFilter,
            this.statusFilter,
            this.targetAudienceRegionFilter
        ];

        return filters;
    }

    /**
     * Updates the page criteria
     */
    setPageCriteria(pageCriteria: PageCriteriaModelInterface, search: boolean = true): void {
        this.page = pageCriteria.page;
        if (this.page <= this.totalNumberOfPages && search) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            this.doSearch();
        }
    }

    /**
     * Checks that the page criteria can be updated
     * @NOTE This is an observable so that users can make the decision via dialogs etc.
     */
    canSetPageCriteria(): Observable<boolean> {
        return of(true);
    }

    public doSearch(
        refreshPrimaryFacets: boolean = false,
        refreshFilteredFacets: boolean = false,
    ): Observable<TargetingSearchResponseApiModel> {
        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }

        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }

        if (this.viewExclusions) {
            this.cachedFilters = filters?.clone(false);
            this.clearFilters();
            filters = new TargetingSearch();
            filters.contactIdsNames.include = this.excludedRecipients
                .map(x => x.contactId)
                .filter(x => !!x)
                .map(x => new IdNamePairModel(null, x));
            filters.outletIdsNames.include = this.excludedRecipients
                .map(x => x.outletId)
                .filter(x => !!x)
                .map(x => new IdNamePairModel(null, x));
        } else if (this.cachedFilters) {
            this.clearFilters();
            filters = this.cachedFilters;
            this.cachedFilters = null;
            this.populateFiltersFromSearch(filters);
            this.applyFilters(false);
        }

        const mainSearchRequest: Observable<TargetingSearchResponseApiModel> = this.fastSearch(this.search, filters);

        // just to make sure it runs, in case the caller doesn't subscribe
        this.searchLoading$.next(true);
        this.mainSearchRequest = mainSearchRequest.subscribe(
            (test) => {
                this.mainSearchRequest = null;
                this.searchLoading$.next(false);
            },
            (err) => {
                console.log(err);
            }
        );

        let filteredFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshFilteredFacets) {
            if (this.filteredFacetRequest) {
                this.filteredFacetRequest.unsubscribe();
            }
            filteredFacetRequest = this.facetSearch(this.search, filters);

            this.filteredFacetRequest = filteredFacetRequest.subscribe(collection => {
                this.setFilteredFacets(collection);
                this.filteredFacetRequest = null;
            });
        }

        if (refreshPrimaryFacets) {
            this.getFacets(filteredFacetRequest);
        }

        return mainSearchRequest;
    }

    getFacets(filteredFacetRequest?: Observable<TargetingFacetCollection>) {
        let primaryFacetRequest: Observable<TargetingFacetCollection>;
        if (this.primaryFacetRequest) {
            this.primaryFacetRequest.unsubscribe();
        }
        if (this.hasFiltersToApply() || !filteredFacetRequest) {
            primaryFacetRequest = this.facetSearch(this.search);
        } else {
            primaryFacetRequest = filteredFacetRequest;
        }

        this.primaryFacetRequest = primaryFacetRequest?.subscribe(collection => {
            this.setPrimaryFacets(collection);
            this.primaryFacetRequest = null;
        });
    }

    // Fast search is just the results without any facets
    fastSearch(
        search: TargetingSearch,
        filters?: TargetingSearch
    ): Observable<TargetingSearchResponseApiModel> {
        const rows = this.pageCriteria.pageSize;
        const start = (this.pageCriteria.page - 1) * rows;
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        const searchRequest: Observable<TargetingSearchResponseApiModel> = this.distributionApiService.performRecommendedRecipientSearch(
            search.toApiModel(false, filters).query,
            start,
            rows,
            sortOrder,
            sortField
        );

        return searchRequest.pipe(
            switchMap(async response => {
                if (this.hasFiltersToApply() || this.isQuickEdit) {
                    // We do not want to cache results if filters are applied, since we clear the filters everytime we open the dialog
                    await this.setSearchResultsWithoutCache(response);
                } else {
                    await this.setSearchResults(response);
                }
                return response;
            })
        );
    }

    // We do not want to cache results if filters are applied, since we clear the filters everytime we open the dialog
    async setSearchResultsWithoutCache(response: TargetingSearchResponseApiModel): Promise<void> {
        this.nRecipients$.next(response.numResults);
        this.nResults = response.numResults;
        this.nResults$.next(this.nResults);
        const resultPromises = response.results.map(x => this.targetingResultFactory.createTargetingResult(x));
        this.results = await promiseOnlyResolved(resultPromises);
        this.results$.next(this.results);

        this.cashedResults$.next(this.results);
        if (this.isQuickEdit) {
            this.cachedSearch$.next(this.search);
        } else {
            this.search = this.cachedSearch[this.distributionId];
            this.cachedRecipientCount$.next(response.numResults);
        }
        this.nRecipients$.next(response.numResults || 0);

        this.fetchListMemberships();
        this.searchLoading$.next(false);
    }

    // Override to compute the recipient count once
    async setSearchResults(response: TargetingSearchResponseApiModel): Promise<void> {
        this.nRecipients$.next(response.numResults);
        this.cachedRecipientCount[this.distributionId] = response.numResults;
        this.nResults = response.numResults;
        this.nResults$.next(this.nResults);
        const resultPromises = response.results.map(x => this.targetingResultFactory.createTargetingResult(x));
        this.results = await promiseOnlyResolved(resultPromises);
        this.results$.next(this.results);
        this.cashedResults[this.distributionId] = this.results;
        this.setCachedResults(this.distributionId);
        this.fetchListMemberships();
        this.searchLoading$.next(false);
    }

    clearSearch(): void {
        this.nResults = 0;
        this.nResults$.next(this.nResults);
        this.results = null;
        this.results$.next(this.results);
        this.searchLoading$.next(false);

        this.totalNumberOfPages = 0;
        this.setPageCriteria(this.defaultPageCriteria, false);
    }

    clearResultsAfterSave() {
        this.isQuickEdit = false;
        this.isQuickEditResponse$.next(this.isQuickEdit);
        this.cashedResults[this.distributionId] = null;
        this.cachedSearch[this.distributionId] = null;
        this.cachedRecipientCount[this.distributionId] = 0;
        this.cashedResults$.next(this.cashedResults[this.distributionId]);
        this.cachedSearch$.next(this.cachedSearch[this.distributionId]);
        this.nRecipients$.next(0);
    }

    setCachedResults(distributionId: number) {
        this.isQuickEdit = false;
        this.isQuickEditResponse$.next(this.isQuickEdit);
        this.cashedResults$.next(this.cashedResults[distributionId]);
        this.cachedSearch$.next(this.cachedSearch[distributionId]);
        this.search = this.cachedSearch[distributionId];
        this.cachedRecipientCount$.next(this.cachedRecipientCount[distributionId]);

        this.nRecipients$.next(this.cachedRecipientCount[distributionId] || 0);
    }

    resetSearch() {
        this.isQuickEdit = false;
        this.isQuickEditResponse$.next(this.isQuickEdit);
        this.clearFilters();
        this.clearSearch();
        this.getRecommendedRecipientQuery(this.distributionId);
    }

    getRecipientTotal() {
        return this.nRecipients$;
    }

    getFilteredResultTotal() {
        return this.nResults$;
    }

    getCachedResults() {
        return this.cashedResults$;
    }

    getCachedSearch() {
        return this.cachedSearch$;
    }

    isQuickEditResponse() {
        return this.isQuickEditResponse$;
    }

    getQuickEditPreviewCount() {
        return this.quickEditPreviewCount$;
    }

    getQuickEditPreviewCountLoading() {
        return this.quickEditPreviewCountLoading$;
    }

    getRecommendedFilters(): TargetingSearch {
        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }

        if (this.viewExclusions) {
            this.cachedFilters = filters?.clone(false);
            this.clearFilters();
            filters = new TargetingSearch();
            filters.contactIdsNames.include = this.excludedRecipients
                .map(x => x.contactId)
                .filter(x => !!x)
                .map(x => new IdNamePairModel(null, x));
            filters.outletIdsNames.include = this.excludedRecipients
                .map(x => x.outletId)
                .filter(x => !!x)
                .map(x => new IdNamePairModel(null, x));
        } else if (this.cachedFilters) {
            this.clearFilters();
            filters = this.cachedFilters;
            this.cachedFilters = null;
            this.populateFiltersFromSearch(filters);
            this.applyFilters(false);
        }
        return filters;
    }

    createRecommendedList(): Observable<number> {
        return this.targetingListsApiService.create({ listType: TargetingListTypeEnum.DISTRIBUTION_RECOMMENDED_RECIPIENT });
    }

    addRecipientsToList(recommendedListId: number, selectedResults: TargetingResultModel[]): Observable<void> {
        return this.targetingListsApiService.addEntitiesToList(
            [recommendedListId],
            selectedResults.map(x => x.toEntityIdModel())
        );
    }

    getRecommendedList(recommendedListId: number): Observable<TargetingListApi> {
        return this.targetingListsApiService.getById(recommendedListId, true);
    }

    addRecipientsAndGetList(recommendedListId: number, selectedResults: TargetingResultModel[]): Observable<TargetingListApi> {
        return this.addRecipientsToList(recommendedListId, selectedResults).pipe(
            mergeMap(() => {
                return this.getRecommendedList(recommendedListId);
            }),
        );
    }

    addSearchResultsToList(recommendedListId: number, filters: TargetingSearch) {
        return this.targetingListsApiService.addSearchResultsToList(
            [recommendedListId],
            this.search.toApiModel(false, filters).query,
            TargetingListTypeEnum.DISTRIBUTION_RECOMMENDED_RECIPIENT
        );
    }

    addSearchAndGetList(recommendedListId: number, filters: TargetingSearch): Observable<TargetingListApi> {
        return this.addSearchResultsToList(recommendedListId, filters).pipe(
            mergeMap(() => {
                return this.getRecommendedList(recommendedListId);
            }),
        );
    }

    removeRecipientsFromList(recommendedListId: number, selectedResults: TargetingResultModel[]): Observable<void> {
        return this.targetingListsApiService.removeEntitiesFromList(
            recommendedListId,
            selectedResults.map(x => x.toEntityIdModel())
        );
    }

    addRecipients(isSearch: boolean, selectedResults?: TargetingResultModel[]) {
        let recommendedRecipientRequest: Observable<any>;
        if (this.recommendedRecipientRequest) {
            this.recommendedRecipientRequest.unsubscribe();
            this.recommendedRecipientRequest = null;
        }

        const recommendedListId = this.recommenedRecipientGroup?.recipients?.[0].targetingListId;
        this.searchLoading$.next(true);

        if (isSearch) {
            const filters = this.getRecommendedFilters();
            if (recommendedListId) {
                recommendedRecipientRequest = this.addSearchAndGetList(recommendedListId, filters);
            } else {
                let newId;
                recommendedRecipientRequest = this.createRecommendedList().pipe(
                    mergeMap(id => {
                        newId = id;
                        return this.addSearchAndGetList(newId, filters);
                    }),
                );
            }
        } else {
            if (recommendedListId) {
                recommendedRecipientRequest = this.addRecipientsAndGetList(recommendedListId, selectedResults);
            } else {
                let newId;
                recommendedRecipientRequest = this.createRecommendedList().pipe(
                    mergeMap(id => {
                        newId = id;
                        return this.addRecipientsAndGetList(newId, selectedResults);
                    }),
                );
            }
        }

        this.recommendedRecipientRequest = recommendedRecipientRequest.subscribe({
            next: (recommendedList) => {
                this.successfullyUpdatedList(true, recommendedList);
            }, error: (e) => {
                this.failedUpdatingList(true);
            }
        });
    }

    removeRecipients(isSearch: boolean, selectedResults?: TargetingResultModel[]) {
        const recommendedListId = this.recommenedRecipientGroup?.recipients?.[0].targetingListId;

        if (recommendedListId) {
            let recommendedRecipientRequest: Observable<any>;
            this.searchLoading$.next(true);
            if (this.recommendedRecipientRequest) {
                this.recommendedRecipientRequest.unsubscribe();
                this.recommendedRecipientRequest = null;
            }

            if (isSearch) {
                const filters = this.getRecommendedFilters();

                recommendedRecipientRequest = this.targetingListsApiService.removeEntitiesMatchingSearch(
                    recommendedListId,
                    this.search.toApiModel(false, filters).query
                ).pipe(
                    mergeMap(() => {
                        return this.getRecommendedList(recommendedListId);
                    })
                );
            } else {
                recommendedRecipientRequest = this.removeRecipientsFromList(recommendedListId, selectedResults).pipe(
                    mergeMap(() => {
                        return this.getRecommendedList(recommendedListId);
                    })
                );
            }

            this.recommendedRecipientRequest = recommendedRecipientRequest.subscribe({
                next: (recommendedList) => {
                    this.successfullyUpdatedList(false, recommendedList);
                }, error: (e) => {
                    this.failedUpdatingList(false);
                }
            });
        }
    }

    successfullyUpdatedList(isAdd: boolean, recommendedList: RecipientRow) {
        let summary = 'Discover.recommendedRecipient.recipient.add.header';
        let detail = 'Discover.recommendedRecipient.recipient.add.message';

        if (!isAdd) {
            summary = 'Discover.recommendedRecipient.recipient.remove.header';
            detail = 'Discover.recommendedRecipient.recipient.remove.message';
        }

        this.recipientEditorService.updateRecommendedRecipient(recommendedList);
        this.fetchListMemberships();

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary,
            detail
        });
        this.searchLoading$.next(false);
    }

    failedUpdatingList(isAdd: boolean) {
        let summary = 'Discover.recommendedRecipient.recipient.add.header';
        let detail = 'Discover.recommendedRecipient.recipient.add.error';

        if (!isAdd) {
            summary = 'Discover.recommendedRecipient.recipient.remove.header';
            detail = 'Discover.recommendedRecipient.recipient.remove.error';
        }
        this.messageService.add({
            severity: messageSeverityEnum.error,
            summary,
            detail
        });
        this.searchLoading$.next(false);
    }
}
